import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import ArticlesGrid, { ArticleHeader } from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isDoctorRecruiterAccount,
  isLicenseVerified,
  isPublisherAccount,
} from '../../../common/libs/user';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import { SPECIALS } from '../../../common/constants';
import useSpecialPage from '../../../hooks/useSpecialPage';
import FullWidthBox from '../../../components/FullWidthBox';
import Breadcrumbs from '../../../components/Breadcrumbs';
import {
  Kenshui30Quiz,
  kenshui30ArticleIds,
  kenshui30Categories,
} from '../../../common/libs/kenshui30';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { generateS3DownloadUrl } from '../../../server/libs/siteSettings';

export const setting = {
  specialPageSlug: SPECIALS.kenshui30.name,
  specialPageShortName: 'kenshu30',
  specialPagePath: SPECIALS.kenshui30.link,
  pageTitle: SPECIALS.kenshui30.alt,
  mainImagePath: SPECIALS.kenshui30.mainImage ?? '',
};

const specialPageSlug = setting.specialPageSlug;
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内

const CategorySection = ({
  number,
  articles,
  title,
  description,
  isMobile,
  quizList,
  isLastItem,
}: {
  number: number;
  articles: ArticleHeader[];
  title: string;
  description?: string;
  isMobile: boolean;
  quizList?: Kenshui30Quiz[];
  isLastItem?: boolean; // ページ最下部の余白調整用
}) => {
  return (
    <Box>
      <Flex
        wrap={{
          base: 'wrap',
          md: 'nowrap',
        }}
        alignItems="center"
        gap={2}
      >
        <Heading
          as="h2"
          fontSize={{
            sm: 'lg',
            md: 'xl',
          }}
          px={4}
          py={2}
          borderLeft="4px solid"
          borderColor="brand.500"
        >
          {number}.{title}
        </Heading>
        <Text fontSize="sm">{description}</Text>
      </Flex>
      <Flex direction={'column'} mt={4}>
        <ArticlesGrid
          articles={articles}
          isMobile={isMobile}
          gaEventLabel={`${specialPageSlug}_articles`}
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `special_${specialPageSlug}#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `special_${specialPageSlug}#${articleId}`,
            );
          }}
        />
      </Flex>
      {quizList?.length && (
        <FullWidthBox
          mt={12}
          pt={12}
          pb={isLastItem ? 14 : 8}
          bg="backgroundLight"
          display="flex"
          justifyContent="center"
          flexDirection="column"
        >
          <Container>
            <Box py={4}>
              <VStack gap={4} alignItems="center">
                <Image
                  src="/images/special/kenshui30_q_image.svg"
                  alt=""
                  width={118}
                />
                <Text fontSize="2xl" fontWeight="bold" textAlign="center">
                  スライド作者からの挑戦！ このクイズに答えられますか？
                </Text>
              </VStack>
              <VStack gap={4} alignItems="center" py={2}>
                {quizList.map(({ title, url }, index) => (
                  <Link
                    color="inherit"
                    _hover={{
                      textDecoration: 'none',
                    }}
                    onClick={() => {
                      sendAnalyticsEvent(
                        'special_contents',
                        'click',
                        `${setting.specialPageShortName}_quiz_${number}_${
                          index + 1
                        }`,
                      );
                    }}
                    key={title}
                    href={url}
                    isExternal
                    target="_blank"
                    rel="noopener noreferrer"
                    display="block"
                    w="full"
                  >
                    <Flex
                      w="full"
                      backgroundColor="white"
                      rounded="lg"
                      boxShadow="lg"
                      p={4}
                      gap={2}
                      alignItems="center"
                      justifyContent="flex-start"
                      flexDirection={{
                        base: 'column',
                        md: 'row',
                      }}
                    >
                      <Image
                        src="/images/special/kenshui30_nikkei_medical_logo.jpg"
                        alt="日経メディカルアイコン"
                        width={73}
                      />
                      <Flex alignItems="center">
                        <Text fontWeight="bold">{title}</Text>
                        <Image
                          src="/images/special/kenshui30_hdouble_arrow.svg"
                          alt="arrow"
                        />
                      </Flex>
                    </Flex>
                  </Link>
                ))}
              </VStack>
            </Box>
          </Container>
        </FullWidthBox>
      )}
    </Box>
  );
};

type PageContext = {
  articles: ArticleHeader[];
  isMobile: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isDemoAccount: boolean;
  isPrivate: boolean;
};

const Kenshui30Page: NextPage<PageContext> = ({
  articles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
}) => {
  const { pathname } = useRouter();
  const isArticlesPage = pathname.split('/')[3] === 'articles';

  const { pageTitle, specialPagePath, specialPageShortName, mainImagePath } =
    setting;

  const specialPageTitle = isArticlesPage ? `${pageTitle} 一覧` : pageTitle;

  const { isBlocked, blockedContent, pageHead } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
    specialPageSlug,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white" mb={-16}>
        <Box
          py={4}
          pl={[4, 8, 4]}
          bg="white"
          w={{ base: '1024px', sm: '1024px' }}
        >
          <Breadcrumbs
            parentPages={[
              {
                title: '特集',
                href: '/special',
                gaLabel: 'special',
              },
              ...(isArticlesPage
                ? [
                    {
                      title: pageTitle,
                      href: specialPagePath,
                      gaLabel: `special_${specialPageShortName}`,
                    },
                  ]
                : []),
            ]}
            currentPageTitle={isArticlesPage ? '一覧' : pageTitle}
          />
        </Box>
        {isArticlesPage ? (
          <Box py={4} px={[4, 6, 0]}>
            <Heading fontSize={26} mb={6}>
              「研修医1年目必見スライド30選」一覧
            </Heading>
            <ArticlesGrid
              articles={articles}
              isMobile={isMobile}
              gaEventLabel={`${specialPageSlug}_articles`}
              responsibleColumns={[1, 3, 4]}
              onShowThumbnail={(articleId) => {
                sendAnalyticsEvent(
                  'slide_thumbnail',
                  'show',
                  `special_${specialPageSlug}#${articleId}`,
                );
              }}
              onClick={(articleId) => {
                sendAnalyticsEvent(
                  'slide_thumbnail',
                  'click',
                  `special_${specialPageSlug}#${articleId}`,
                );
              }}
            />
            <Box pt={10} pb={12} textAlign="center">
              <NextLink href="/special/kenshui30" passHref>
                <Button
                  as="a"
                  colorScheme="brand"
                  size="lg"
                  fontSize="xl"
                  width={{ base: 'full', md: 'auto' }}
                  whiteSpace="pre-wrap"
                  py={3}
                  height="auto"
                >
                  「研修医1年目必読スライド30選」特集TOPへ
                </Button>
              </NextLink>
            </Box>
          </Box>
        ) : (
          <>
            <FullWidthBox
              bg={'white'}
              display="flex"
              justifyContent="center"
              flexDirection="column"
            >
              <Container>
                <Flex
                  color="white"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                >
                  <Image
                    src="/images/special/kenshui30_top_title.png"
                    alt="研修医1年目 必見スライド30選 先輩医師が「1年目で役立った」スライドを紹介"
                    width={496}
                    maxW={{ base: '100%', md: '48%' }}
                  />
                  <Image
                    src={
                      isMobile
                        ? '/images/special/kenshui30_top_illustration_sm.png'
                        : '/images/special/kenshui30_top_illustration_pc.png'
                    }
                    alt="全6カテゴリ/30スライドを厳選して紹介！「これだけは見ておきたい」をまとめました！"
                    width={523}
                    maxW={{ base: '100%', md: '52%' }}
                  />
                </Flex>
              </Container>
            </FullWidthBox>
            <FullWidthBox
              bg={'primaryMain'}
              display="flex"
              justifyContent="center"
              flexDirection="column"
            >
              <Container>
                <Flex
                  color="white"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  py={6}
                >
                  <NextLink href={'/events/new-residents-orientation'} passHref>
                    <Link
                      backgroundColor="white"
                      px={8}
                      _hover={{
                        opacity: 0.8,
                      }}
                      onClick={() => {
                        sendAnalyticsEvent(
                          'special_contents',
                          'click',
                          'new-residents-orientation',
                        );
                      }}
                      borderRadius="lg"
                    >
                      <Image
                        src={
                          isMobile
                            ? generateS3DownloadUrl(
                                'special_articles/new-residents-orientation/new-residents-orientation_head_mobile.png',
                              )
                            : generateS3DownloadUrl(
                                'special_articles/new-residents-orientation/new-residents-orientation_head_pc.png',
                              )
                        }
                        alt="Antaa 人気スライド講師が教える 2026年度 新・研修医向け 1dayオリエンテーション"
                        w="full"
                      />
                    </Link>
                  </NextLink>
                </Flex>
              </Container>
            </FullWidthBox>
            <FullWidthBox
              bg={'#EEF6ED'}
              display="flex"
              justifyContent="center"
              flexDirection="column"
            >
              <Container>
                <VStack
                  gap={4}
                  alignItems="center"
                  px={{
                    base: 0,
                    md: 8,
                  }}
                  pt={{
                    base: 6,
                    md: 8,
                  }}
                  pb={{
                    base: 4,
                    md: 8,
                  }}
                >
                  <NextLink href="/special/kenshui30/articles" passHref>
                    <Button
                      as="a"
                      colorScheme="brand"
                      size="lg"
                      fontSize="xl"
                      width={{ base: 'full', md: 'auto' }}
                    >
                      全30スライドを一覧で見る
                    </Button>
                  </NextLink>
                  <Grid
                    templateColumns={{
                      base: '58px 1fr',
                      md: '83px 1fr',
                    }}
                    gridRowGap={2}
                    gridColumnGap={{
                      base: 2,
                      md: 4,
                    }}
                    backgroundColor="white"
                    py={{
                      base: 4,
                      md: 8,
                    }}
                    px={{
                      base: 2,
                      md: 8,
                    }}
                  >
                    <GridItem
                      gridRow={{
                        base: '1 / 2',
                        md: '1 / 3',
                      }}
                    >
                      <Image
                        src="/images/special/kenshui30_doctor_sakamoto.jpg"
                        alt="坂本壮先生"
                        height={{
                          base: 58,
                          md: 83,
                        }}
                        rounded="full"
                      />
                    </GridItem>
                    <GridItem
                      fontSize={{
                        base: 'sm',
                        md: 'lg',
                      }}
                      fontWeight="bold"
                    >
                      悩める初期研修医へ。玉石混交の情報の中、同じ悩みを越えた先輩たちの力作スライドから、確かな知識と現場で役立つ視点を手に入れよう！
                    </GridItem>
                    <GridItem
                      gridColumn={{
                        base: '1 / 3',
                        md: '2 / 3',
                      }}
                      fontSize={{
                        base: 'xs',
                        md: 'sm',
                      }}
                      textAlign={{
                        base: 'center',
                        md: 'left',
                      }}
                    >
                      国保旭中央病院　救命救急センター医長　坂本壮先生
                    </GridItem>
                  </Grid>
                </VStack>
              </Container>
            </FullWidthBox>
            <Flex px={[4, 6, 0]} pt={9} gap={9} direction="column">
              {kenshui30Categories.map((category, index) => {
                const categoryArticles = articles.filter((article) =>
                  category.articleIds.includes(article.articleId),
                );
                return (
                  <CategorySection
                    key={category.title}
                    number={index + 1}
                    articles={categoryArticles}
                    isMobile={isMobile}
                    isLastItem={index === kenshui30Categories.length - 1}
                    {...category}
                  />
                );
              })}
            </Flex>
          </>
        )}
      </Box>
    </>
  );
};

export default Kenshui30Page;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let articles: ArticleHeader[] = [];
    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: kenshui30ArticleIds,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    // アカウント統合済みのユーザのプロフィールのみ返却される
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    articles = articleHeaders.reduce((articles: any, article: any) => {
      const author = article.user_article_header_author_idTouser;
      const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

      if (
        shouldNotShow(
          idBaseProfile,
          context.req.user,
          context.req.isAuthenticated(),
          article,
        )
      ) {
        return articles;
      }

      articles.push({
        articleId: article.article_id,
        name: article.name,
        articleType: article.article_type,
        status: article.status as ArticleStatus | null,
        topImageM: article.top_img_m,
        sessionCount: article.session_count || 0,
        likeCount:
          article.like_count +
          article.article_page.reduce(
            (prev: any, curr: any) => prev + curr.like_count,
            0,
          ),
        commentCount: article.article_comments.length,
        discloseScope: article.disclose_scope as DiscloseScope | null,
        createDate: date.toIso8601Jst(article.create_date),
        updateDate: date.toIso8601Jst(article.update_date),
        author: {
          userId: article.user_article_header_author_idTouser.user_id,
          userName: idBaseProfile?.userName ?? null,
          // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
          lastName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.lastName ?? author.last_name,
          firstName: idBaseProfile?.userName
            ? null
            : idBaseProfile?.firstName ?? author.first_name,
          updateDateEpochMilliseconds:
            article.user_article_header_author_idTouser.update_date.getTime(),
          profilePhotoS3Key:
            article.user_article_header_author_idTouser.profile_photo_s3_key,
          idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
          antaaAccountType: idBaseProfile?.accountType ?? null,
          antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
          isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
        },
        tags: article.article_tag_connection.map((conn: any) => ({
          id: conn.tag.tag_id,
          name: conn.tag.tag_name,
        })),
      });
      return articles;
    }, []);

    articles.sort((a, b) => {
      return (
        kenshui30ArticleIds.indexOf(a.articleId) -
        kenshui30ArticleIds.indexOf(b.articleId)
      );
    });

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
